import {
	StepRunStatus,
	WorkfrontBundleState,
	WorkfrontBundleSyncStatus,
	WorkfrontRunStatus,
	WorkfrontSyncErrorSeverity,
	WorkfrontSyncErrorStatus,
} from '@atlas/types'
import { Badge, type BadgeProps } from '@atlas/ui-primitives'

type Variant = NonNullable<BadgeProps['variant']>

/** `awaiting_parent` → `awaiting parent`: codes read as words, without a closed label map. */
export const humanize = (code: string): string => code.replace(/_/g, ' ')

/** Seconds as the coarsest unit that keeps it readable: `45 s`, `12 min`, `3 h 5 min`. */
export function formatDuration(seconds: number): string {
	if (seconds < 60) return `${seconds} s`
	const minutes = Math.round(seconds / 60)
	if (minutes < 60) return `${minutes} min`
	return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
}

const BUNDLE_STATE: Record<string, Variant> = {
	[WorkfrontBundleState.ACTIVE]: 'success',
	[WorkfrontBundleState.AWAITING_PARENT]: 'warning',
	[WorkfrontBundleState.AMBIGUOUS]: 'danger',
	[WorkfrontBundleState.CLOSED]: 'muted',
	[WorkfrontBundleState.OUT_OF_SCOPE]: 'muted',
	[WorkfrontBundleState.REMOVED]: 'muted',
}

const SYNC_STATUS: Record<string, Variant> = {
	[WorkfrontBundleSyncStatus.CLEAN]: 'muted',
	[WorkfrontBundleSyncStatus.DIRTY]: 'warning',
	[WorkfrontBundleSyncStatus.FETCHING]: 'secondary',
	[WorkfrontBundleSyncStatus.FAILED]: 'danger',
	[WorkfrontBundleSyncStatus.HELD]: 'danger',
}

const RUN_STATUS: Record<string, Variant> = {
	[WorkfrontRunStatus.RUNNING]: 'secondary',
	[WorkfrontRunStatus.SUCCEEDED]: 'success',
	[WorkfrontRunStatus.PARTIAL]: 'warning',
	[WorkfrontRunStatus.FAILED]: 'danger',
	[StepRunStatus.PENDING]: 'muted',
	[StepRunStatus.HELD]: 'danger',
	[StepRunStatus.SKIPPED]: 'muted',
}

const ERROR_STATUS: Record<string, Variant> = {
	[WorkfrontSyncErrorStatus.OPEN]: 'danger',
	[WorkfrontSyncErrorStatus.RETRYING]: 'secondary',
	[WorkfrontSyncErrorStatus.RESOLVED]: 'success',
	[WorkfrontSyncErrorStatus.IGNORED]: 'muted',
}

const SEVERITY: Record<string, Variant> = {
	[WorkfrontSyncErrorSeverity.ERROR]: 'danger',
	[WorkfrontSyncErrorSeverity.WARNING]: 'warning',
}

function StatusBadge({ value, variants }: { value: string; variants: Record<string, Variant> }) {
	return (
		<Badge size="sm" variant={variants[value] ?? 'muted'}>
			{humanize(value)}
		</Badge>
	)
}

export const BundleStateBadge = ({ state }: { state: string }) => <StatusBadge value={state} variants={BUNDLE_STATE} />
export const SyncStatusBadge = ({ status }: { status: string }) => <StatusBadge value={status} variants={SYNC_STATUS} />
/** A run's or a step run's status: the two share their words. */
export const RunStatusBadge = ({ status }: { status: string }) => <StatusBadge value={status} variants={RUN_STATUS} />
/**
 * An error's status. An open problem is coloured by its severity, so an open warning
 * does not read as an error; every other status has one colour.
 */
export function ErrorStatusBadge({ status, severity }: { status: string; severity?: string }) {
	const open = status === WorkfrontSyncErrorStatus.OPEN
	const variants =
		open && severity ? { ...ERROR_STATUS, [status]: SEVERITY[severity] ?? ERROR_STATUS[status] } : ERROR_STATUS
	return <StatusBadge value={status} variants={variants} />
}
export const SeverityBadge = ({ severity }: { severity: string }) => (
	<StatusBadge value={severity} variants={SEVERITY} />
)
